<template>
    <div class="loginPage">
        <transition name="fadeInOut">
            <!-- Login form -->
            <form v-if="!impersonateContainerShowing" @submit.prevent="preLogin">
                <h1>Leads</h1>
                <div class="form-group">
                    <label for="username">User Name</label>
                    <text-input v-model="username.input" type="text" name="username" />
                    <span v-if="username.error != null" class="errorMessage">{{
                        username.error
                    }}</span>
                </div>
                <div class="form-group">
                    <label for="password">Password</label>
                    <text-input v-model="password.input" type="password" name="password" />
                    <span v-if="password.error != null" class="errorMessage">{{
                        password.error
                    }}</span>
                </div>
                <app-button type="submit" class="btn btn-green" text="Log In" />
                <transition name="slide">
                    <div class="error"></div>
                </transition>
            </form>

            <!-- Impersonate form -->
            <form v-else class="impersonate-container">
                <h1>Login as?</h1>

                <div class="form-group">
                    <multi-select
                        v-model="selectedIdentity"
                        :options="loginAsOptions"
                        track-by="encompassID"
                        label="fullName"
                        :searchable="false"
                        :show-labels="false"
                        :hide-selected="true"
                        placeholder="Select Person"
                        class="impersonateSelector"
                    />
                    <span v-if="impersonateError != null" class="errorMessage">{{
                        impersonateError
                    }}</span>
                </div>

                <app-button
                    type="button"
                    class="btn btn-green"
                    text="Continue"
                    @click.native="selectImpersonation"
                />
            </form>
        </transition>

        <!-- Agreements Modal -->
        <base-dialog
            v-if="showAgreements"
            v-model="showAgreements"
            :modal="true"
            :enable-light-dismiss="false"
        >
            <template #default>
                <div class="agreements-content">
                    <div class="agreements-title-subtitle">
                        <h2>User Agreements</h2>
                        <p>Please review and agree to the following terms.</p>
                    </div>
                    <ol class="agreements-list">
                        <li>
                            If you can’t reach out to the borrower immediately, please DO NOT accept
                            the lead.
                        </li>
                        <li>
                            Please be sure to check the Referral Source and confirm with the
                            borrower that they were not referred to a specific LO.
                        </li>
                        <li>
                            If the lead was meant for a different LO and needs to be reassigned, the
                            Help Desk can get you back into first place on the Leads platform.
                        </li>
                    </ol>
                    <div class="agreements-button">
                        <app-button
                            class="btn btn-orange"
                            text="Cancel"
                            @click.native="disagreeAndExit"
                        />

                        <app-button
                            class="btn btn-green"
                            text="I Agree"
                            @click.native="agreeAndProceed"
                        />
                    </div>
                </div>
            </template>
        </base-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import api from "@/api";

export default {
    name: "Login",
    components: {
        "text-input": () => import("@/components/Inputs/TextInput"),
        "app-button": () => import("@/components/AppButton"),
        "multi-select": () => import("@/components/Inputs/Multiselect/Multiselect"),
        "base-dialog": () => import("@/components/Dialog/BaseDialog"),
    },
    data() {
        return {
            username: {
                input: "",
                error: null,
            },
            password: {
                input: "",
                error: null,
            },
            loginIsValid: true,
            impersonateContainerShowing: false,
            localSession: null,
            selectedIdentity: {},
            impersonateError: null,
            showAgreements: false,
        };
    },
    computed: {
        ...mapState(["userProfile"]),

        loginAsOptions() {
            const options = JSON.parse(JSON.stringify(this.localSession.loProfiles));
            options.push(this.localSession.session);
            return options;
        },
    },
    methods: {
        ...mapActions(["setUserProfile", "setAppLoading", "setSession", "logOut"]),

        trimString(value) {
            return value.trim();
        },

        disagreeAndExit() {
            this.impersonateContainerShowing = false;
            this.showAgreements = false;
        },

        validateLogin() {
            this.loginIsValid = true;
            if (this.trimString(this.username.input) === "") {
                this.loginIsValid = false;
                this.username.error = "Please enter your username.";
            } else {
                this.username.error = null;
            }
            if (this.trimString(this.password.input) === "") {
                this.loginIsValid = false;
                this.password.error = "Please enter your password.";
            } else {
                this.password.error = null;
            }
        },

        async preLogin() {
            await this.validateLogin();
            if (this.loginIsValid) {
                this.setAppLoading(true);
                api.auth
                    .login({
                        userID: this.username.input,
                        password: this.password.input,
                    })
                    .then(async (response) => {
                        const { data } = response;
                        await this.setUserProfile(data);
                        // if this user is an LOA, set up the prompt for who they are logging in as
                        if (data.loProfiles.length > 0) {
                            this.localSession = JSON.parse(JSON.stringify({ ...data }));
                            if (this.localSession.canOriginate === true) {
                                this.selectedIdentity = {
                                    encompassID: this.localSession.session.encompassID,
                                    fullName: this.localSession.session.fullName,
                                    activeDirectoryID: this.localSession.session.activeDirectoryID,
                                };
                            } else {
                                // dont allow them to login as self if they cant originate
                                this.selectedIdentity = null;
                            }
                            this.impersonateContainerShowing = true;
                        } else {
                            this.showAgreements = true;
                        }
                        this.setAppLoading(false);
                    })
                    .catch((error) => {
                        let errorText;
                        if (error?.response?.status == 400) {
                            errorText = error.response.data;
                        }
                        this.setAppLoading(false);
                        this.$notify({
                            group: "bottom-left",
                            title: "Login Failed",
                            text:
                                errorText ||
                                "There was a problem logging in. Please try again or contact the help desk.",
                            type: "error",
                        });
                    });
            }
        },

        async selectImpersonation() {
            this.impersonateError = null;
            this.setAppLoading(true);
            // validate lo select input before proceeding
            if (this.selectedIdentity !== null) {
                // if user is logging in as themselves
                if (this.selectedIdentity.encompassID == this.localSession.session.encompassID) {
                    this.showAgreements = true;
                } else {
                    // user is impersonating an LO
                    this.localSession.session.impersonatedLO = this.selectedIdentity;
                    api.auth
                        .setImpersonatedLoanOfficer(this.localSession.session)
                        .then((response) => {
                            // tell the API that user is impersonating so session data can be updated
                            this.setSession(response.data);
                            this.showAgreements = true;
                        })
                        .catch((error) => {
                            let errorText;
                            if (error?.response?.status == 400) {
                                errorText = error.response.data;
                            }
                            this.$notify({
                                group: "bottom-left",
                                title: "Login Failed",
                                text:
                                    errorText ||
                                    `There was a problem logging in as ${this.selectedIdentity.fullName}. Please try again or contact the help desk.`,
                                type: "error",
                            });
                            this.impersonateContainerShowing = false;
                        });
                }
            } else {
                this.impersonateError = "A Loan officer is required.";
            }
            this.setAppLoading(false);
        },

        agreeAndProceed() {
            localStorage.setItem("agreementsAccepted", "true");
            this.showAgreements = false;
            // we reload page here to ensure browser downloads newly deployed code
            window.location.href = "/";
        },

        async loginFromMobile(userID, token) {
            this.setAppLoading(true);
            api.auth
                .loginFromMobile({ userID, token })
                .then(async (response) => {
                    const { data } = response;
                    data.token = {};
                    data.token.access_token = token;
                    await this.setUserProfile(data);
                    // if this user is an LOA, set up the prompt for who they are logging in as
                    if (data.loProfiles.length > 0) {
                        this.localSession = JSON.parse(JSON.stringify({ ...data }));
                        if (this.localSession.canOriginate === true) {
                            this.selectedIdentity = {
                                encompassID: this.localSession.session.encompassID,
                                fullName: this.localSession.session.fullName,
                                activeDirectoryID: this.localSession.session.activeDirectoryID,
                            };
                        } else {
                            // dont allow them to login as self if they cant originate
                            this.selectedIdentity = null;
                        }
                        this.impersonateContainerShowing = true;
                    } else {
                        this.showAgreements = true;
                    }
                    this.setAppLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                    let errorText;
                    if (error?.response?.status == 400) {
                        errorText = error.response.data;
                    }
                    this.setAppLoading(false);
                    this.$notify({
                        group: "bottom-left",
                        title: "Login Failed",
                        text:
                            errorText ||
                            "There was a problem logging in. Please try again or contact the help desk.",
                        type: "error",
                    });
                });
        },
    },

    mounted() {
        // check for mobile login handoff
        localStorage.setItem("agreementsAccepted", "false");
        const userID = this.$route.query.userID;
        const token = this.$route.query.token;
        if (userID && token) {
            this.loginFromMobile(userID, token);
        }
    },
};
</script>

<style lang="scss" scoped>
.loginPage {
    height: 100vh;
    background-color: $lightGray;
    background-image: url("~@/assets/images/loginPage_bg.jpg");
    background-size: cover;
    background-repeat: no-repeat;
}

h1 {
    margin-bottom: 2rem;
    text-align: center;
    font-size: 3rem;
    color: $lightTeal;
}

form {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 37rem;
    padding: 2rem 6rem 2rem;
    background-color: #fff;
    box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.3);
    @include breakpoint(mobile) {
        width: 96%;
    }
    label {
        color: #333;
    }
    button {
        width: 15rem;
        display: block;
        margin: 3rem auto 2rem;
    }
    .error {
        position: absolute;
        bottom: 0rem;
        padding: 1rem 6rem 1rem 0;
        text-align: center;
        font-size: 1.4rem;
        color: $red;
        font-weight: 600;
    }
}

// TRANSITIONS
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}
.slide-enter,
.slide-leave-to {
    transform: translateX(-3rem);
    opacity: 0;
}

.agreements-content {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    row-gap: 2rem;
    justify-content: center;
    align-items: center;
    .agreements-title-subtitle {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        row-gap: 0.5rem;
        line-height: 1;
        h2 {
            color: #333;
        }
        p {
            color: #666;
        }
    }
    .agreements-list {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        row-gap: 1rem;
        margin-bottom: 1rem;
        text-align: left;
        max-width: 40rem;
        padding: 2rem;
        li {
            text-wrap: pretty;
            line-height: 1.2;
            color: #666;
        }
    }

    .agreements-button {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        column-gap: 3rem;

        button {
            width: 10rem;
        }
    }
}
</style>
